import { uniq } from 'lodash'
import moment from 'moment'

import { DayLeaf, MunchedTree } from 'modules/timeoff/components/YearlyCalendar/Months/store'

export type Range = 'start' | 'middle' | 'end' | 'start-end' | 'outline'

enum DayLeafClass {
  LEAVE = '',
  BLOCKED_PERIOD = '',
  COMPANY_HOLIDAY = 'wrong',
}

/**
 * A DateTree looks like this:
 *
 * {
 *   2016: {
 *     1: {
 *       3: ['primary'],
 *       4: ['brand']
 *     }
 *     10: {
 *       22: ['brand'],
 *       23: ['brand']
 *      }
 *   }
 * }
 */
export type DaysTree = Record<number, Record<number, Record<number, string[]>>>
/**
 * Finds whether the given day is a a day present on the
 * days tree.
 *
 * We structure the days as a tree so we can efficiently can
 * find wether the given day is present or not.
 *
 * We return a "range" which is a representation
 * of how that day is present. We do so we can paint them
 * accordingly with CSS.
 *
 * Start and end of week also provoke `start` and `end` edges.
 *
 * Examples of ranges:
 *
 *       +-middle      +-start-end
 *       v             v
 *   (1  2  3  4)  5  (6)  7
 *    ^        ^
 *    +-start  +-end
 */

export default function getRange(dateTree: DaysTree, day: moment.Moment): string[] {
  const months = dateTree[day.year()]

  if (!months) return []

  const daysOfMonth = months[day.month() + 1]

  if (!daysOfMonth) return []

  const dayOfMonth = day.date()

  if (!daysOfMonth[dayOfMonth]) return []

  const hasNextDay = daysOfMonth[dayOfMonth + 1] // last day of month
  const hasPrevDay = daysOfMonth[dayOfMonth - 1] // first day of month
  const isStartWeek = day.weekday() === 0
  const isEndWeek = day.weekday() === 6
  const isToday = moment().dayOfYear() === day.dayOfYear()

  function classesForStartWeek(): Range {
    if (isToday) return 'outline'

    if (hasNextDay) return 'start'

    return 'start-end'
  }

  function classesForEndWeek(): Range {
    if (isToday) return 'outline'

    if (hasPrevDay) return 'end'

    return 'start-end'
  }

  function classesForMiddleWeek(): Range {
    if (isToday) return 'outline'

    if (hasNextDay && hasPrevDay) return 'middle'

    if (hasPrevDay) return 'end'

    if (hasNextDay) return 'start'

    return 'start-end'
  }

  function getRange(): Range[] {
    const result: Range[] = []

    if (isStartWeek) {
      result.push(classesForStartWeek())
    } else if (isEndWeek) {
      result.push(classesForEndWeek())
    } else {
      result.push(classesForMiddleWeek())
    }

    return result
  }

  // This add the existing classes to the ones we just calculated
  return (daysOfMonth[dayOfMonth] ?? []).concat(getRange())
}

function dayLeafToClasses(dayLeaf: DayLeaf): DayLeafClass[] {
  const result: DayLeafClass[] = []
  if (dayLeaf.leaves.length) result.push(DayLeafClass.LEAVE)
  if (dayLeaf.blockedPeriods.length) result.push(DayLeafClass.BLOCKED_PERIOD)
  if (dayLeaf.companyHolidays.length) result.push(DayLeafClass.COMPANY_HOLIDAY)
  return uniq(result)
}

/*
  munchedTreeToDayTree map a MunchedTree with the following structure
  {
    '2020-01-01': {
      blockedPeriods: [blockedPeriod, blockedPeriod, blockedPeriod],
      companyHolidays: [companyHoliday],
      leaves: [leave, leave, leave],
    },
    '2020-01-02': {
      blockedPeriods: [],
      companyHolidays: [companyHoliday],
      leaves: [],
    }
    ....
  }

  To a DaysTree with the following structure:
  {
    2020: {
      1: {
        1: ['', 'wrong'],
        2: ....
      },
      2: {
        1: ['wrong'],
        ......
      }
    }
    ......
  }
*/
export function munchedTreeToDayTree(tree: MunchedTree): DaysTree {
  return Object.keys(tree).reduce<DaysTree>((memo, key) => {
    const splitDate = key.split('-').map((d) => parseInt(d, 10))
    const year = splitDate[0] as number
    const month = splitDate[1] as number
    const dayOfMonth = splitDate[2] as number

    memo[year] = memo[year] || {}
    memo[year]![month] = memo[year]![month] || {}
    memo[year]![month]![dayOfMonth] = dayLeafToClasses(tree[key] as DayLeaf)
    return memo
  }, {})
}
